import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import PatientUtils from 'services/PatientUtils';
import { PatientImage } from 'components/Common/UserImage';
import GenericBadge from 'components/Utils/GenericBadge';
import './style.scss';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { NavLink } from 'react-router-dom';
import RouteUtils from 'services/RouteUtils';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import Icon from 'components/Utils/NewIcon';
import { IsPatient } from 'careand-redux/selectors';

const DEFAULT_ARRAY = [];

/**
 * @param {any} props
 */
const PatientBadge = ({
  patient,
  onClick,
  id,
  goToProfile = false,
  className,
  goToBubble = false,
}) => {
  const dispatch = useAppDispatch();

  const statePatient = useAppSelector((state) => state.patients.byId[id]);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const isPatient = useAppSelector(IsPatient);
  const patientBubbleId = useAppSelector(
    (state) => state.ui.bubbles.find((it) => it.patient_id === (id ?? patient?.id))?.id,
  );

  const [currentPatient, setCurrentPatient] = useState(patient || statePatient);
  const [loading, setLoading] = useState(false);
  const clinicId = patient?.clinic_id;

  const clinic = useMemo(() => {
    if (!isPatient) return clinics.find((clinic) => clinic.id === clinicId);
  }, [clinics, clinicId, isPatient]);

  useEffect(() => {
    if (!!id && !currentPatient) {
      setLoading(true);
      dispatch(api.Patients.getPatient({ id })).then((patient) => {
        setLoading(false);
        if (patient) setCurrentPatient(patient);
      });
    }
  }, [dispatch, id, currentPatient]);

  useEffect(() => {
    if (patient) setCurrentPatient(patient);
  }, [patient]);

  const route = useMemo(() => {
    if (goToBubble && patientBubbleId) {
      return RouteUtils.getRoute(RouteUtils.routes().bubble, {
        ':bubbleId': patientBubbleId,
      });
    }
    if (goToProfile && currentPatient)
      return RouteUtils.getRoute(RouteUtils.routes().patient, {
        ':patientId': currentPatient.id,
      });
  }, [currentPatient, goToProfile, goToBubble, patientBubbleId]);

  return route ? (
    <NavLink to={route}>
      <BadgeContent
        patient={currentPatient}
        onClick={onClick}
        loading={loading}
        clinic={clinic}
        className={className}
      />
    </NavLink>
  ) : (
    <BadgeContent
      patient={currentPatient}
      onClick={onClick}
      loading={loading}
      clinic={clinic}
      includeShortName={!isPatient}
      className={className}
    />
  );
};

const BadgeContent = ({
  patient,
  onClick,
  loading,
  clinic,
  includeShortName = true,
  className,
}) => {
  const ownId = useAppSelector((state) => state.authentication.user.id);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const favoritePatients = physicians[ownId]?.favorite_patients || DEFAULT_ARRAY;

  const isFavorite = useMemo(() => {
    return !!favoritePatients?.includes(patient?.id);
  }, [favoritePatients, patient]);

  return (
    <GenericBadge
      id={patient ? `patient-badge-${patient.id}` : undefined}
      className={'patient-badge ' + (className ?? '')}
      title={PatientUtils.getName(patient, 'Unknown Patient', includeShortName) || ''}
      avatar={
        <>
          <PatientImage user={patient} size={40} />
          <ProgressMessage size={36} topLeft={true} show={loading} />
          {isFavorite && <Icon width={20} className="favorite-icon" src={['fas', 'star']} />}
        </>
      }
      info={<InfoText patient={patient} clinic={clinic} />}
      onClick={onClick ? () => onClick(patient) : undefined}
    />
  );
};

const InfoText = ({ patient, clinic }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <div className="gender-age-wrapper">
      <span className="gender-age">
        {PatientUtils.getGender(patient, true)} {PatientUtils.getBirthdate(patient, 'YYYY/MM/DD')}
      </span>{' '}
      <span>{PatientUtils.getOhipNumber(patient, 'No OHIP card')}</span>
    </div>
    {!!clinic && <span>{clinic?.name}</span>}
  </div>
);

PatientBadge.propTypes = {
  id: PropTypes.string,
  patient: PropTypes.object,
  appointment: PropTypes.object,
  onClick: PropTypes.func,
  goToProfile: PropTypes.bool,
};

export default PatientBadge;
